import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Swords, Plus, Trophy, Trash2, Crown, X } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TournamentBracket = ({ isDark }) => {
  const { token } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [viewingTournament, setViewingTournament] = useState(null);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('single_elimination');
  const [playerInputs, setPlayerInputs] = useState(['', '']);
  const [creating, setCreating] = useState(false);

  useEffect(() => { fetchTournaments(); }, []);

  const fetchTournaments = async () => {
    try {
      const res = await axios.get(`${API}/tournaments/`, { headers: { Authorization: `Bearer ${token}` } });
      setTournaments(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    const players = playerInputs.filter(p => p.trim());
    if (!newName.trim() || players.length < 2) {
      toast({ title: 'Need a name and at least 2 players', variant: 'destructive' });
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/tournaments/`, { name: newName, players, bracket_type: newType }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Tournament created!' });
      setShowCreate(false);
      setNewName('');
      setPlayerInputs(['', '']);
      fetchTournaments();
    } catch (e) {
      toast({ title: 'Failed to create', variant: 'destructive' });
    } finally { setCreating(false); }
  };

  const handleResult = async (tournamentId, matchId, winner) => {
    try {
      const res = await axios.put(`${API}/tournaments/${tournamentId}/result`, { match_id: matchId, winner }, { headers: { Authorization: `Bearer ${token}` } });
      setViewingTournament(res.data);
      fetchTournaments();
    } catch (e) {
      toast({ title: 'Failed to update result', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/tournaments/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Tournament deleted' });
      setViewingTournament(null);
      fetchTournaments();
    } catch (e) { toast({ title: 'Failed to delete', variant: 'destructive' }); }
  };

  const addPlayer = () => setPlayerInputs(prev => [...prev, '']);
  const removePlayer = (i) => setPlayerInputs(prev => prev.filter((_, idx) => idx !== i));
  const updatePlayer = (i, v) => setPlayerInputs(prev => prev.map((p, idx) => idx === i ? v : p));

  if (loading) return <div className="flex justify-center h-64 items-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="tournament-page">
      <div className="flex items-center justify-between">
        <h2 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : ''}`}><Swords className="h-5 w-5 text-indigo-600" /> Tournaments</h2>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-tournament-btn"><Plus className="h-4 w-4 mr-1" /> New</Button>
      </div>

      {/* Tournament list */}
      {tournaments.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-gray-500">
          <Swords className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p>No tournaments yet. Create one!</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {tournaments.map(t => (
            <Card key={t.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setViewingTournament(t)} data-testid={`tournament-${t.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm">{t.name}</p>
                  <p className="text-xs text-gray-500">{t.players.length} players · {t.bracket_type.replace('_', ' ')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={t.status === 'complete' ? 'default' : 'secondary'}>{t.status === 'complete' ? 'Complete' : 'Active'}</Badge>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); handleDelete(t.id); }}>
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View tournament bracket */}
      <Dialog open={!!viewingTournament} onOpenChange={() => setViewingTournament(null)}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trophy className="h-5 w-5 text-yellow-500" /> {viewingTournament?.name}</DialogTitle>
          </DialogHeader>
          {viewingTournament && (
            <div className="space-y-3">
              {/* Group by round */}
              {[...new Set(viewingTournament.matches.map(m => m.round))].sort().map(round => (
                <div key={round}>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Round {round}</p>
                  <div className="space-y-2">
                    {viewingTournament.matches.filter(m => m.round === round).map(m => (
                      <div key={m.id} className={`p-3 rounded-lg border ${m.winner ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <button
                              onClick={() => !m.winner && m.player1 !== 'TBD' && m.player2 !== 'TBD' && m.player2 !== 'BYE' && handleResult(viewingTournament.id, m.id, m.player1)}
                              disabled={!!m.winner || m.player1 === 'TBD'}
                              className={`block w-full text-left text-sm p-1.5 rounded ${m.winner === m.player1 ? 'font-bold text-green-700' : ''} ${!m.winner && m.player1 !== 'TBD' ? 'hover:bg-green-100 cursor-pointer' : ''}`}
                            >
                              {m.winner === m.player1 && <Crown className="h-3 w-3 inline mr-1 text-yellow-500" />}
                              {m.player1}
                            </button>
                            <div className="text-xs text-gray-400 text-center my-0.5">vs</div>
                            <button
                              onClick={() => !m.winner && m.player2 !== 'TBD' && m.player2 !== 'BYE' && handleResult(viewingTournament.id, m.id, m.player2)}
                              disabled={!!m.winner || m.player2 === 'TBD' || m.player2 === 'BYE'}
                              className={`block w-full text-left text-sm p-1.5 rounded ${m.winner === m.player2 ? 'font-bold text-green-700' : ''} ${!m.winner && m.player2 !== 'TBD' && m.player2 !== 'BYE' ? 'hover:bg-green-100 cursor-pointer' : ''}`}
                            >
                              {m.winner === m.player2 && <Crown className="h-3 w-3 inline mr-1 text-yellow-500" />}
                              {m.player2}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Create Tournament</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Tournament Name</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Sunday Smashdown" className="mt-1" data-testid="tournament-name-input" />
            </div>
            <div>
              <Label className="text-xs">Format</Label>
              <Select value={newType} onValueChange={setNewType}>
                <SelectTrigger className="mt-1" data-testid="tournament-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single_elimination">Single Elimination</SelectItem>
                  <SelectItem value="round_robin">Round Robin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Players</Label>
              <div className="space-y-2 mt-1">
                {playerInputs.map((p, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={p} onChange={(e) => updatePlayer(i, e.target.value)} placeholder={`Player ${i + 1}`} className="h-8" data-testid={`player-input-${i}`} />
                    {playerInputs.length > 2 && (
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => removePlayer(i)}><X className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addPlayer} className="w-full"><Plus className="h-3.5 w-3.5 mr-1" /> Add Player</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="create-tournament-submit">{creating ? 'Creating...' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TournamentBracket;
